import {
  AutoAwesome as AgentIcon,
  CheckCircle as DecisionIcon,
  Close as CloseIcon,
  NotificationsActive as SignalIcon,
  PersonAdd as CandidateIcon,
  SwapHoriz as StateChangeIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import type { AccountStreamMatch, ActivityFeedItem, FeedEventType } from "../../types/accountIntelligence";
import {
  MOCK_ACCOUNT_STREAM_MATCHES,
  MOCK_ACTIVITY_FEED,
  MOCK_STREAMS,
  getICPLabel,
} from "./mockData";
import AccountWorkspace from "./AccountWorkspace";

// ─── Feed config ──────────────────────────────────────────────────────────────

const FEED_CONFIG: Record<FeedEventType, { icon: React.ReactNode; color: string; label: string }> = {
  signal_detected: { icon: <SignalIcon sx={{ fontSize: 15 }} />, color: "#f9a825", label: "Signal" },
  new_candidate: { icon: <CandidateIcon sx={{ fontSize: 15 }} />, color: "#9e9e9e", label: "New Candidate" },
  opportunity_identified: { icon: <AgentIcon sx={{ fontSize: 15 }} />, color: "#1976d2", label: "Opportunity" },
  user_status_change: { icon: <StateChangeIcon sx={{ fontSize: 15 }} />, color: "#e65100", label: "Status Change" },
  decision_made: { icon: <DecisionIcon sx={{ fontSize: 15 }} />, color: "#2e7d32", label: "Decision" },
};

// ─── Date grouping ────────────────────────────────────────────────────────────

function getDateGroup(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "This Week";
  if (days < 14) return "Last Week";
  return "Earlier";
}

const DATE_GROUP_ORDER = ["Today", "Yesterday", "This Week", "Last Week", "Earlier"];

function formatTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// ─── Account detail drawer ────────────────────────────────────────────────────

const AccountDetailDrawer: React.FC<{
  match: AccountStreamMatch | null;
  onClose: () => void;
}> = ({ match, onClose }) => (
  <Drawer
    anchor="right"
    open={!!match}
    onClose={onClose}
    PaperProps={{ sx: { width: { xs: "100vw", sm: 680 }, backgroundColor: "background.paper", backgroundImage: "none" } }}
  >
    {match && (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <AccountWorkspace accountId={match.account.id} streamId={match.streamId} onBack={onClose} />
      </Box>
    )}
  </Drawer>
);

// ─── Feed item ────────────────────────────────────────────────────────────────

const FeedItem: React.FC<{
  item: ActivityFeedItem;
  onOpenAccount: (accountId: string, streamId: string) => void;
  dismissed: Set<string>;
  onDismiss: (id: string) => void;
  onConfirm: (id: string) => void;
}> = ({ item, onOpenAccount, dismissed, onDismiss, onConfirm }) => {
  const cfg = FEED_CONFIG[item.type];
  const icpLabel = getICPLabel(item.streamId);
  const isActioned = dismissed.has(item.id);

  if (isActioned) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, py: 1.5 }}>
      {/* Icon */}
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          backgroundColor: `${cfg.color}18`,
          border: `1px solid ${cfg.color}35`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: cfg.color,
          flexShrink: 0,
          mt: 0.1,
        }}
      >
        {cfg.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              cursor: "pointer",
              "&:hover": { color: "primary.light", textDecoration: "underline" },
            }}
            onClick={() => onOpenAccount(item.accountId, item.streamId)}
          >
            {item.accountName}
          </Typography>
          <Chip label={icpLabel} size="small" sx={{ fontSize: "0.65rem", height: 18, color: "text.secondary" }} />
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: 18,
              color: cfg.color,
              backgroundColor: `${cfg.color}15`,
              border: `1px solid ${cfg.color}30`,
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" mb={item.requiresDecision ? 1 : 0}>
          {item.description}
        </Typography>

        {/* Inline decision buttons for items that need action */}
        {item.requiresDecision && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => onConfirm(item.id)}
              sx={{ textTransform: "none", fontSize: "0.72rem" }}
            >
              Accept
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onDismiss(item.id)}
              sx={{ textTransform: "none", fontSize: "0.72rem" }}
            >
              Dismiss
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => onOpenAccount(item.accountId, item.streamId)}
              sx={{ textTransform: "none", fontSize: "0.72rem", color: "text.secondary" }}
            >
              View account
            </Button>
          </Box>
        )}
      </Box>

      {/* Timestamp */}
      <Typography variant="caption" color="text.disabled" flexShrink={0} mt={0.25}>
        {formatTime(item.date)}
      </Typography>
    </Box>
  );
};

// ─── Main IntelligenceFeed ────────────────────────────────────────────────────

const IntelligenceFeed: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState<FeedEventType | "all">("all");
  const [streamFilter, setStreamFilter] = useState<string | "all">("all");
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [selectedMatch, setSelectedMatch] = useState<AccountStreamMatch | null>(null);

  const openAccount = (accountId: string, streamId: string): void => {
    const match = MOCK_ACCOUNT_STREAM_MATCHES.find(
      (m) => m.account.id === accountId && m.streamId === streamId,
    );
    if (match) setSelectedMatch(match);
  };

  const dismiss = (id: string): void =>
    setDismissed((prev) => new Set(Array.from(prev).concat(id)));

  // Apply filters
  const filtered = MOCK_ACTIVITY_FEED.filter((item) => {
    if (typeFilter !== "all" && item.type !== typeFilter) return false;
    if (streamFilter !== "all" && item.streamId !== streamFilter) return false;
    return true;
  });

  // Group by date
  const grouped = filtered.reduce<Record<string, ActivityFeedItem[]>>((acc, item) => {
    const group = getDateGroup(item.date);
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const pendingDecisions = MOCK_ACTIVITY_FEED.filter(
    (f) => f.requiresDecision && !dismissed.has(f.id),
  ).length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          <Typography variant="h5" fontWeight={700}>
            Feed
          </Typography>
          {pendingDecisions > 0 && (
            <Chip
              label={`${pendingDecisions} awaiting decision`}
              size="small"
              sx={{ fontSize: "0.72rem", color: "primary.main", backgroundColor: "rgba(25,118,210,0.1)", border: "1px solid rgba(25,118,210,0.3)" }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          All account intelligence activity
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          px: 3,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Type filter */}
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setTypeFilter("all")}
            sx={{
              fontSize: "0.72rem",
              cursor: "pointer",
              backgroundColor: typeFilter === "all" ? "primary.main" : undefined,
              color: typeFilter === "all" ? "white" : "text.secondary",
            }}
          />
          {(Object.entries(FEED_CONFIG) as [FeedEventType, typeof FEED_CONFIG[FeedEventType]][]).map(
            ([type, cfg]) => (
              <Chip
                key={type}
                label={cfg.label}
                size="small"
                onClick={() => setTypeFilter(typeFilter === type ? "all" : type)}
                sx={{
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  backgroundColor: typeFilter === type ? cfg.color : undefined,
                  color: typeFilter === type ? "white" : "text.secondary",
                }}
              />
            ),
          )}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* ICP / stream filter */}
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" color="text.disabled" fontWeight={600}>
            ICP:
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setStreamFilter("all")}
            sx={{
              fontSize: "0.72rem",
              cursor: "pointer",
              backgroundColor: streamFilter === "all" ? "primary.main" : undefined,
              color: streamFilter === "all" ? "white" : "text.secondary",
            }}
          />
          {MOCK_STREAMS.map((s) => (
            <Chip
              key={s.id}
              label={s.icp.name.split(" ").slice(0, 3).join(" ")}
              size="small"
              onClick={() => setStreamFilter(streamFilter === s.id ? "all" : s.id)}
              sx={{
                fontSize: "0.72rem",
                cursor: "pointer",
                backgroundColor: streamFilter === s.id ? "primary.main" : undefined,
                color: streamFilter === s.id ? "white" : "text.secondary",
                maxWidth: 200,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Feed list */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 1 }}>
        {DATE_GROUP_ORDER.filter((g) => grouped[g]?.length).map((group) => (
          <Box key={group} mb={2}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.disabled"
              sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {group}
            </Typography>
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, px: 2 }}>
              {grouped[group].map((item, i) => (
                <React.Fragment key={item.id}>
                  <FeedItem
                    item={item}
                    onOpenAccount={openAccount}
                    dismissed={dismissed}
                    onDismiss={dismiss}
                    onConfirm={dismiss}
                  />
                  {i < grouped[group].length - 1 && !dismissed.has(item.id) && <Divider />}
                </React.Fragment>
              ))}
            </Paper>
          </Box>
        ))}

        {filtered.length === 0 && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography color="text.secondary">No events match the current filters.</Typography>
          </Box>
        )}
      </Box>

      {/* Drawer */}
      <AccountDetailDrawer match={selectedMatch} onClose={() => setSelectedMatch(null)} />
    </Box>
  );
};

export default IntelligenceFeed;
